import L from 'leaflet';
import { useEffect, useRef, useState, type FC } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import {
  layoutPins,
  PIN_LEADER_THRESHOLD,
  pinOffset,
} from '@/app/agent/surfaces/guide-pin-layout.ts';
import { trpc } from '@/app/lib/trpc';

const messages = defineMessages({
  loading: {
    id: 'destinationShortlist.mapLoading',
    defaultMessage: 'Locating this city and its guide places…',
    description: 'Loading status shown over the OpenStreetMap guide map',
  },
  unavailable: {
    id: 'destinationShortlist.mapUnavailable',
    defaultMessage: 'The live map could not locate this city right now.',
    description: 'Fallback status shown when the OpenStreetMap geocoder cannot locate the guide city',
  },
  radiusCaption: {
    id: 'destinationShortlist.mapRadiusCaption',
    defaultMessage: '{city} · guide places within {radius} km',
    description: 'Caption on the guide map naming the city and the radius the map covers',
  },
});

export type GuideMapMarker = {
  /** Unique identity for this individual map pin, not its category. */
  key: string;
  /** Guide section used to update the matching card on the right. */
  sectionKey: string;
  name: string;
  category: string;
  area: string;
  markerClass: string;
  priceTag?: string;
  rank?: number;
  selected: boolean;
};

type GeocodedLocation = {
  key: string;
  name: string;
  latitude: number;
  longitude: number;
  displayName: string;
};

type GeocodingResponse = {
  city: GeocodedLocation | null;
  locations: GeocodedLocation[];
  radiusKm?: number;
  source: string;
};

type OpenStreetMapGuideMapProps = {
  city: string;
  country: string;
  markers: GuideMapMarker[];
  onMarkerSelect: (key: string, name: string) => void;
  ariaLabel: string;
};

const TILE_URL = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';
const FALLBACK_TILE_URL = 'https://tile.openstreetmap.de/{z}/{x}/{y}.png';

/** Mirrors the server's guide radius; only used if a response omits it. */
const DEFAULT_RADIUS_KM = 20;
// Marker sets grow in bursts while a guide is merged into its card; one short
// pause collapses that into a single geocoding round trip.
const GEOCODE_DEBOUNCE_MS = 250;
const TILE_ATTRIBUTION =
  '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">OpenStreetMap</a> contributors';

const LEAFLET_STYLE = `
.wayfarer-osm-map .leaflet-pane,
.wayfarer-osm-map .leaflet-tile,
.wayfarer-osm-map .leaflet-marker-icon,
.wayfarer-osm-map .leaflet-marker-shadow,
.wayfarer-osm-map .leaflet-tile-container,
.wayfarer-osm-map .leaflet-map-pane svg,
.wayfarer-osm-map .leaflet-map-pane canvas,
.wayfarer-osm-map .leaflet-zoom-box { position: absolute; left: 0; top: 0; }
.wayfarer-osm-map .leaflet-container { position: relative; width: 100%; height: 100%; overflow: hidden; font: inherit; background: #e8e4dc; outline: 0; }
.wayfarer-osm-map .leaflet-container img,
.wayfarer-osm-map .leaflet-tile-pane img,
.wayfarer-osm-map .leaflet-marker-pane img,
.wayfarer-osm-map .leaflet-shadow-pane img,
.wayfarer-osm-map .leaflet-tile { max-width: none !important; max-height: none !important; padding: 0; border: 0; }
.wayfarer-osm-map .leaflet-tile { visibility: visible; filter: saturate(.82) contrast(.96); }
.wayfarer-osm-map .leaflet-tile-pane { z-index: 2; }
.wayfarer-osm-map .leaflet-overlay-pane { z-index: 4; }
.wayfarer-osm-map .leaflet-shadow-pane { z-index: 5; }
.wayfarer-osm-map .leaflet-marker-pane { z-index: 6; }
.wayfarer-osm-map .leaflet-tooltip-pane { z-index: 7; }
.wayfarer-osm-map .leaflet-popup-pane { z-index: 8; }
.wayfarer-osm-map .leaflet-map-pane { z-index: 0; }
.wayfarer-osm-map .leaflet-zoom-animated { transform-origin: 0 0; }
.wayfarer-osm-map .leaflet-control { position: relative; z-index: 800; pointer-events: auto; float: left; clear: both; }
.wayfarer-osm-map .leaflet-top { top: 0; }
.wayfarer-osm-map .leaflet-bottom { bottom: 0; }
.wayfarer-osm-map .leaflet-left { left: 0; }
.wayfarer-osm-map .leaflet-right { right: 0; }
.wayfarer-osm-map .leaflet-top .leaflet-control { margin-top: 10px; }
.wayfarer-osm-map .leaflet-bottom .leaflet-control { margin-bottom: 10px; }
.wayfarer-osm-map .leaflet-left .leaflet-control { margin-left: 10px; }
.wayfarer-osm-map .leaflet-right .leaflet-control { margin-right: 10px; float: right; }
.wayfarer-osm-map .leaflet-bar { box-shadow: 0 1px 5px rgba(15, 23, 42, .18); border-radius: 8px; overflow: hidden; }
.wayfarer-osm-map .leaflet-bar a { display: block; width: 30px; height: 30px; border-bottom: 1px solid #d8d2c8; background: rgba(255,255,255,.94); color: #334155; text-align: center; text-decoration: none; font-size: 22px; line-height: 27px; }
.wayfarer-osm-map .leaflet-bar a:last-child { border-bottom: 0; }
.wayfarer-osm-map .leaflet-bar a:hover { background: #f7f4ed; }
/* OSM attribution is required for these tiles; keep it present but quiet inside the popup. */
.wayfarer-osm-map .leaflet-control-attribution { margin: 0 4px 4px 0; padding: 1px 5px; border-radius: 999px; color: #475569; background: rgba(255,255,255,.72); font-size: 8px; line-height: 1.2; opacity: .78; }
.wayfarer-osm-map .leaflet-control-attribution a { color: #334155; }
.wayfarer-osm-map .leaflet-tooltip { position: absolute; padding: 4px 8px; border: 1px solid #d8d2c8; border-radius: 999px; background: rgba(255,255,255,.96); color: #1e293b; box-shadow: 0 1px 4px rgba(15,23,42,.12); white-space: nowrap; font-size: 11px; font-weight: 600; pointer-events: none; }
.wayfarer-osm-map .leaflet-tooltip-top:before { position: absolute; bottom: -5px; left: 50%; margin-left: -5px; border: 5px solid transparent; border-top-color: #d8d2c8; content: ''; }
`;

function escapeHtml(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

/**
 * Pins are laid out with the shared collision pass in `guide-pin-layout.ts`; see
 * that module for why and how. Here we only translate between Leaflet's pixel
 * space and its plain-object footprints.
 */
function createMarkerIcon(marker: GuideMapMarker, showPrice: boolean): L.DivIcon {
  const circleClasses = [
    'absolute',
    'left-1/2',
    'top-0',
    '-translate-x-1/2',
    'flex',
    'h-9',
    'w-9',
    'items-center',
    'justify-center',
    'rounded-full',
    'border-2',
    'border-travel-shortlist-card',
    'shadow-lg',
    marker.markerClass,
    marker.selected ? 'z-10 scale-110 ring-2 ring-primary ring-offset-2 ring-offset-travel-shortlist-background' : '',
  ]
    .filter(Boolean)
    .join(' ');
  const innerLabel = marker.rank ? `<span class="sr-only">${marker.rank}. </span>` : '';
  const pricePill =
    marker.priceTag && showPrice
      ? `<span class="absolute left-1/2 top-10 -translate-x-1/2 whitespace-nowrap rounded-full border border-travel-shortlist-border bg-travel-shortlist-card px-2 py-0.5 text-center text-[11px] font-semibold leading-tight text-travel-shortlist-foreground shadow-sm">${escapeHtml(marker.priceTag)}</span>`
      : '';
  return L.divIcon({
    className: '!border-0 !bg-transparent',
    html: `<span class="relative block h-[62px] w-40" aria-label="${escapeHtml(marker.name)}"><span class="${circleClasses}" aria-hidden="true"><span class="h-2.5 w-2.5 rounded-full bg-travel-shortlist-card"></span>${innerLabel}</span>${pricePill}</span>`,
    iconSize: [160, 62],
    iconAnchor: [80, 18],
  });
}

export const OpenStreetMapGuideMap: FC<OpenStreetMapGuideMapProps> = ({
  city,
  country,
  markers,
  onMarkerSelect,
  ariaLabel,
}) => {
  const intl = useIntl();
  const mapElementRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markerLayerRef = useRef<L.LayerGroup | null>(null);
  const onMarkerSelectRef = useRef(onMarkerSelect);
  const geocodingRef = useRef<GeocodingResponse | null>(null);
  const [geocoding, setGeocoding] = useState<GeocodingResponse | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'unavailable'>('loading');
  const markerSignature = markers
    .map((marker) => `${marker.key}:${marker.name}:${marker.area}`)
    .join('|');

  useEffect(() => {
    onMarkerSelectRef.current = onMarkerSelect;
  }, [onMarkerSelect]);

  useEffect(() => {
    if (!mapElementRef.current || mapRef.current) {
      return;
    }

    const map = L.map(mapElementRef.current, {
      zoomControl: true,
      attributionControl: true,
      minZoom: 2,
      maxZoom: 19,
    });
    const tileLayer = L.tileLayer(TILE_URL, {
      maxZoom: 19,
      attribution: TILE_ATTRIBUTION,
    }).addTo(map);
    const fallbackTileLayer = L.tileLayer(FALLBACK_TILE_URL, {
      maxZoom: 19,
      attribution: TILE_ATTRIBUTION,
    });
    let fallbackActivated = false;
    tileLayer.on('tileerror', () => {
      if (fallbackActivated) {
        return;
      }
      fallbackActivated = true;
      fallbackTileLayer.addTo(map);
    });
    const markerLayer = L.layerGroup().addTo(map);
    mapRef.current = map;
    markerLayerRef.current = markerLayer;
    map.setView([20, 0], 2);

    const frame = window.requestAnimationFrame(() => map.invalidateSize());
    const resizeObserver = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => map.invalidateSize({ pan: false }));
    resizeObserver?.observe(mapElementRef.current);
    return () => {
      window.cancelAnimationFrame(frame);
      resizeObserver?.disconnect();
      markerLayer.clearLayers();
      tileLayer.remove();
      fallbackTileLayer.remove();
      map.remove();
      mapRef.current = null;
      markerLayerRef.current = null;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    // The popup mounts this map before the guide text exists, so the first run
    // geocodes the city alone and frames the town straight away. Later runs add
    // the guide's pins: the previous result is kept on screen while they resolve
    // (blanking it would throw the visitor back to a grey rectangle), and a
    // short debounce collapses the bursts of marker changes that arrive while
    // the guide is being merged into the card.
    const hasPreviousResult = geocodingRef.current !== null;
    if (!hasPreviousResult) {
      setStatus('loading');
    }
    const runQuery = (): void => {
      trpc.placeGeocoding.search
        .query({
          city,
          country,
          places: markers.map((marker) => ({
            key: marker.key,
            name: marker.name,
            area: marker.area,
            category: marker.category,
          })),
        })
        .then((response) => {
          if (cancelled) {
            return;
          }
          geocodingRef.current = response;
          setGeocoding(response);
          setStatus(response.city || response.locations.length > 0 ? 'ready' : 'unavailable');
        })
        .catch((error) => {
          if (cancelled) {
            return;
          }
          console.warn('[OpenStreetMapGuideMap] Geocoding failed', {
            message: error instanceof Error ? error.message : 'unknown error',
          });
          if (!geocodingRef.current) {
            setStatus('unavailable');
          }
        });
    };
    const delay = hasPreviousResult ? GEOCODE_DEBOUNCE_MS : 0;
    const timeoutId = window.setTimeout(runQuery, delay);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [city, country, markerSignature]);

  useEffect(() => {
    const map = mapRef.current;
    const markerLayer = markerLayerRef.current;
    if (!map || !markerLayer || !geocoding) {
      return;
    }

    const locationsByKey = new Map(geocoding.locations.map((location) => [location.key, location]));
    const pinned = markers
      .map((marker) => ({ marker, location: locationsByKey.get(marker.key) }))
      .filter((entry): entry is { marker: GuideMapMarker; location: GeocodedLocation } => Boolean(entry.location));
    const visibleLocations = pinned.map(
      (entry) => [entry.location.latitude, entry.location.longitude] as [number, number],
    );

    // Pins are laid out in screen space, so the whole set is re-placed whenever
    // the zoom changes: at street level they sit on their exact spots, and only
    // when the view tightens do they start stepping aside for one another.
    const drawPins = (): void => {
      markerLayer.clearLayers();
      const zoom = map.getZoom();
      const markersByKey = new Map(pinned.map((entry) => [entry.marker.key, entry.marker]));
      const layout = layoutPins(
        pinned.map((entry) => {
          const projected = map.project([entry.location.latitude, entry.location.longitude], zoom);
          return {
            key: entry.marker.key,
            base: { x: projected.x, y: projected.y },
            priceTag: entry.marker.priceTag ?? '',
          };
        }),
      );

      for (const placement of layout.footprints) {
        const marker = markersByKey.get(placement.key);
        if (!marker) {
          continue;
        }
        const basePosition = map.unproject(L.point(placement.base.x, placement.base.y), zoom);
        const pinPosition = map.unproject(L.point(placement.point.x, placement.point.y), zoom);
        const nudged = pinOffset(placement) > PIN_LEADER_THRESHOLD;

        if (nudged) {
          L.polyline([basePosition, pinPosition], {
            weight: 1.5,
            opacity: 0.55,
            color: '#475569',
            dashArray: '3 4',
            interactive: false,
          }).addTo(markerLayer);
          L.circleMarker(basePosition, {
            radius: 2.5,
            weight: 1,
            color: '#475569',
            opacity: 0.7,
            fillColor: '#475569',
            fillOpacity: 0.7,
            interactive: false,
          }).addTo(markerLayer);
        }

        const leafletMarker = L.marker(pinPosition, {
          icon: createMarkerIcon(marker, !layout.compact),
          title: marker.name,
          alt: `${marker.name}${marker.category ? ` · ${marker.category}` : ''}`,
          riseOnHover: true,
        });
        leafletMarker.bindTooltip(
          layout.compact && marker.priceTag
            ? `${escapeHtml(marker.name)} · ${escapeHtml(marker.priceTag)}`
            : escapeHtml(marker.name),
          {
            direction: 'top',
            offset: [0, -18],
            sticky: true,
          },
        );
        leafletMarker.on('click', () => onMarkerSelectRef.current(marker.sectionKey, marker.name));
        leafletMarker.addTo(markerLayer);
      }
    };

    const cityPoint = geocoding.city
      ? ([geocoding.city.latitude, geocoding.city.longitude] as [number, number])
      : null;
    const radiusKm = geocoding.radiusKm && geocoding.radiusKm > 0 ? geocoding.radiusKm : DEFAULT_RADIUS_KM;

    // The city itself is the anchor: the view opens on it and never widens past
    // the guide radius, so a customer sees this town and its own places rather
    // than half a coastline. Panning and zooming out are bounded to the same
    // circle for the same reason.
    if (cityPoint) {
      const cityLatLng = L.latLng(cityPoint);
      const radiusBounds = cityLatLng.toBounds(radiusKm * 2000);
      map.setMinZoom(2);
      map.setMaxBounds(radiusBounds.pad(0.4));
      if (visibleLocations.length === 0) {
        map.setView(cityLatLng, 13);
      } else {
        map.fitBounds(L.latLngBounds([cityPoint, ...visibleLocations]), {
          padding: [96, 96],
          maxZoom: 15,
        });
      }
      const widestZoom = map.getBoundsZoom(radiusBounds);
      map.setMinZoom(Math.max(2, widestZoom));
      if (map.getZoom() < widestZoom) {
        map.setZoom(widestZoom);
      }
    } else if (visibleLocations.length > 0) {
      map.fitBounds(L.latLngBounds(visibleLocations), {
        padding: [96, 96],
        maxZoom: 15,
      });
    }

    drawPins();
    map.on('zoomend', drawPins);
    window.requestAnimationFrame(() => map.invalidateSize());
    return () => {
      map.off('zoomend', drawPins);
    };
  }, [geocoding, markers]);

  return (
    <div
      className="wayfarer-osm-map relative h-[420px] min-h-[420px] overflow-hidden rounded-[15px] border border-travel-shortlist-border bg-travel-shortlist-background sm:h-[480px]"
      role="application"
      aria-label={ariaLabel}
    >
      <style>{LEAFLET_STYLE}</style>
      <div ref={mapElementRef} className="absolute inset-0 z-0" />
      {status !== 'ready' ? (
        <div className="pointer-events-none absolute inset-x-4 top-4 z-[500] flex items-center justify-center rounded-full border border-travel-shortlist-border bg-travel-shortlist-card/95 px-3 py-2 text-center text-xs font-semibold text-travel-shortlist-foreground shadow-sm">
          {status === 'loading' ? (
            <>
              <span
                className="mr-2 h-3.5 w-3.5 animate-pulse rounded-full border-2 border-travel-shortlist-border border-t-primary"
                aria-hidden="true"
              />
              {intl.formatMessage(messages.loading)}
            </>
          ) : (
            intl.formatMessage(messages.unavailable)
          )}
        </div>
      ) : null}
      <span className="pointer-events-none absolute bottom-3 left-3 z-[500] rounded-full border border-travel-shortlist-border bg-travel-shortlist-card/95 px-2.5 py-1 text-[11px] font-semibold text-travel-shortlist-foreground shadow-sm">
        {status === 'ready' && geocoding?.city
          ? intl.formatMessage(messages.radiusCaption, {
              city: city || country,
              radius: geocoding.radiusKm && geocoding.radiusKm > 0 ? geocoding.radiusKm : DEFAULT_RADIUS_KM,
            })
          : city || country}
      </span>
    </div>
  );
};
